import React, { useRef } from "react";
import { Link } from "react-router-dom";
import { motion, useScroll, useTransform } from "framer-motion";
import Marquee from "react-fast-marquee";
import { ArrowUpRight, ArrowDown } from "lucide-react";
import { useContent } from "@/context/ContentContext";
import { Reveal, MaskedLines } from "@/components/Reveal";

const HeroImg =
  "https://images.unsplash.com/photo-1573339887617-d674bc961c31?crop=entropy&cs=srgb&fm=jpg&q=85&w=1600";

export default function Home() {
  const { content } = useContent();
  const s = content?.settings || {};
  const services = content?.services || [];
  const projects = content?.projects || [];
  const testimonials = content?.testimonials || [];

  const heroRef = useRef(null);
  const { scrollYProgress } = useScroll({ target: heroRef, offset: ["start start", "end start"] });
  const imgY = useTransform(scrollYProgress, [0, 1], ["0%", "30%"]);
  const titleY = useTransform(scrollYProgress, [0, 1], ["0%", "-20%"]);

  const titleLines = (s.hero_title || "We turn ideas into things people can feel.").split(/(?<=\.)\s|(?=and)/).length > 1
    ? [s.hero_title]
    : [s.hero_title];

  return (
    <div data-testid="home-page">
      {/* HERO */}
      <section ref={heroRef} className="relative min-h-screen bg-[#050505] text-white overflow-hidden flex items-end">
        <motion.div style={{ y: imgY }} className="absolute inset-0 z-0">
          <img src={HeroImg} alt="" className="w-full h-[120%] object-cover opacity-45" />
          <div className="absolute inset-0 bg-gradient-to-t from-[#050505] via-[#050505]/40 to-[#050505]/70" />
        </motion.div>

        <motion.div style={{ y: titleY }} className="relative z-10 w-full px-6 md:px-12 lg:px-16 pb-20 pt-36">
          <div className="max-w-[1400px] mx-auto">
            <motion.p
              initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 0.2, duration: 0.8 }}
              className="font-mono-t text-xs md:text-sm uppercase tracking-[0.25em] text-[#D4FF00] mb-8"
            >
              {s.hero_eyebrow || "Print · Brand · Experience — Malaysia"}
            </motion.p>
            <h1 className="font-display font-semibold text-[13vw] leading-[0.92] md:text-[7.5vw] tracking-tighter max-w-[16ch]">
              <MaskedLines lines={[s.hero_title || "We turn ideas into things people can feel."]} delay={0.35} />
            </h1>
            <div className="mt-10 flex flex-col md:flex-row md:items-end justify-between gap-8">
              <p className="text-zinc-300 text-base md:text-lg max-w-md leading-relaxed">
                {s.hero_subtitle}
              </p>
              <Link
                to="/contact"
                data-testid="hero-cta-button"
                className="btn-fill self-start inline-flex items-center gap-2 border border-white text-white px-8 py-4 font-semibold whitespace-nowrap"
              >
                {s.hero_cta || "Start a project"} <ArrowUpRight size={20} />
              </Link>
            </div>
          </div>
        </motion.div>

        <div className="absolute bottom-6 right-6 md:right-16 z-10 flex items-center gap-2 text-zinc-500 text-xs uppercase tracking-widest">
          <ArrowDown size={14} className="animate-bounce" /> Scroll
        </div>
      </section>

      {/* MARQUEE */}
      <section className="bg-[#D4FF00] text-black py-6 border-y border-black">
        <Marquee speed={45} gradient={false} autoFill>
          <span className="font-display text-3xl md:text-5xl tracking-tight px-6">
            {s.marquee_text || "PRINTING · BRANDING · LIVE EVENTS"} ·&nbsp;
          </span>
        </Marquee>
      </section>

      {/* SERVICES */}
      <section className="bg-[#FAFAFA] py-24 md:py-32 px-6 md:px-12 lg:px-16">
        <div className="max-w-[1400px] mx-auto">
          <Reveal className="flex items-end justify-between mb-16 flex-wrap gap-6">
            <div>
              <p className="font-mono-t text-xs uppercase tracking-[0.2em] text-zinc-500 mb-4">What we do</p>
              <h2 className="font-display text-4xl md:text-6xl tracking-tight max-w-2xl">Four disciplines, one lab.</h2>
            </div>
            <Link to="/services" className="link-underline font-medium inline-flex items-center gap-1" data-testid="home-services-link">
              All services <ArrowUpRight size={18} />
            </Link>
          </Reveal>
          <div className="grid grid-cols-1 md:grid-cols-2 border-t border-l border-black/15">
            {services.map((sv, i) => (
              <Reveal key={sv.id} delay={i * 0.05}>
                <div data-testid={`service-card-${i}`} className="group border-b border-r border-black/15 p-8 md:p-12 h-full hover:bg-[#050505] hover:text-white transition-colors duration-500">
                  <div className="flex items-start justify-between">
                    <span className="font-mono-t text-sm text-zinc-500 group-hover:text-[#D4FF00]">0{i + 1}</span>
                    <ArrowUpRight className="opacity-0 group-hover:opacity-100 transition-opacity" size={22} />
                  </div>
                  <h3 className="font-display text-3xl md:text-4xl tracking-tight mt-8">{sv.title}</h3>
                  <p className="font-mono-t text-xs uppercase tracking-widest text-zinc-500 group-hover:text-zinc-400 mt-3">{sv.subtitle}</p>
                  <p className="mt-5 text-zinc-600 group-hover:text-zinc-300 leading-relaxed max-w-md">{sv.description}</p>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* SELECTED WORK */}
      <section className="bg-[#050505] text-white py-24 md:py-32 px-6 md:px-12 lg:px-16">
        <div className="max-w-[1400px] mx-auto">
          <Reveal className="mb-16">
            <p className="font-mono-t text-xs uppercase tracking-[0.2em] text-zinc-500 mb-4">Selected work</p>
            <h2 className="font-display text-4xl md:text-6xl tracking-tight">Recent projects.</h2>
          </Reveal>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6 md:gap-8">
            {projects.slice(0, 4).map((p, i) => (
              <Reveal key={p.id} delay={i * 0.05}>
                <Link to="/portfolio" data-testid={`home-project-${i}`} className={`group block ${i % 2 === 1 ? "md:mt-16" : ""}`}>
                  <div className="overflow-hidden aspect-[4/3] bg-zinc-900">
                    <img src={p.image} alt={p.title} className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105" />
                  </div>
                  <div className="flex items-start justify-between mt-5">
                    <div>
                      <h3 className="font-display text-2xl md:text-3xl tracking-tight">{p.title}</h3>
                      <p className="font-mono-t text-xs uppercase tracking-widest text-zinc-500 mt-2">{p.category} — {p.year}</p>
                    </div>
                    <ArrowUpRight className="opacity-40 group-hover:opacity-100 transition-opacity mt-1" size={22} />
                  </div>
                </Link>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* MANIFESTO */}
      <section className="bg-[#FAFAFA] py-24 md:py-32 px-6 md:px-12 lg:px-16">
        <div className="max-w-[1400px] mx-auto">
          <Reveal><p className="font-display text-3xl md:text-5xl tracking-tight max-w-4xl leading-[1.1]">{s.about_intro}</p></Reveal>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-x-10 gap-y-14 mt-20">
            {(s.chapters || []).map((c, i) => (
              <Reveal key={i} delay={i * 0.08}>
                <div data-testid={`manifesto-chapter-${i}`}>
                  <span className="font-display text-7xl md:text-8xl tracking-tighter text-transparent" style={{ WebkitTextStroke: "1px #050505" }}>{c.number}</span>
                  <h3 className="font-display text-2xl tracking-tight mt-4">{c.title}</h3>
                  <p className="text-zinc-600 mt-3 leading-relaxed">{c.body}</p>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* TESTIMONIALS */}
      <section className="bg-[#121212] text-white py-24 md:py-32 px-6 md:px-12 lg:px-16">
        <div className="max-w-[1400px] mx-auto">
          <Reveal><p className="font-mono-t text-xs uppercase tracking-[0.2em] text-zinc-500 mb-16">Don't take our word for it.</p></Reveal>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-10">
            {testimonials.map((t, i) => (
              <Reveal key={t.id} delay={i * 0.08}>
                <div data-testid={`testimonial-${i}`} className="border-t border-white/15 pt-6">
                  <p className="text-xl md:text-2xl font-display tracking-tight leading-snug">"{t.quote}"</p>
                  <p className="mt-6 text-sm font-medium">{t.author}</p>
                  <p className="text-zinc-500 text-sm">{t.company}</p>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>
    </div>
  );
}
